import GoogleVisionImageAnalyzer from './GoogleVisionImageAnalyzer.js';
import HuggingFaceImageAnalyzer from './HuggingFaceImageAnalyzer.js';

class UniversalImageAnalyzer {
  constructor() {
    this.googleVision = new GoogleVisionImageAnalyzer();
    this.huggingFace = new HuggingFaceImageAnalyzer();
    this.isAnalyzing = false;
    this.requestCount = 0;
    this.successfulService = null;
    
    console.log('🌐 UniversalImageAnalyzer initialized:', {
      googleVisionConfigured: this.googleVision.isConfigured(),
      huggingFaceConfigured: this.huggingFace.isConfigured(),
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Analyze image using the best available service
   */
  async analyzeImage(imageFile, context = {}) {
    const debugId = Math.random().toString(36).substring(7);
    console.log(`🚀 [${debugId}] Starting Universal Image Analysis`);
    
    if (this.isAnalyzing) {
      throw new Error("Analysis already in progress");
    }

    try {
      this.isAnalyzing = true;
      this.requestCount++;
      
      console.log(`📋 [${debugId}] Universal analysis strategy:`, {
        filePresent: !!imageFile,
        fileName: imageFile?.name,
        fileSize: imageFile?.size,
        googleVisionConfigured: this.googleVision.isConfigured(),
        huggingFaceConfigured: this.huggingFace.isConfigured(),
        lastSuccessfulService: this.successfulService,
        requestCount: this.requestCount
      });

      // Strategy: Try services in order of preference and success history
      const services = this.getServiceOrder();
      let lastError = null;
      let bestResult = null;

      for (const service of services) {
        try {
          console.log(`🔄 [${debugId}] Trying ${service.name}...`);
          
          const result = await service.analyzer.analyzeImage(imageFile, context);
          
          if (result.success) {
            console.log(`✅ [${debugId}] Success with ${service.name}:`, {
              description: result.description,
              confidence: result.confidence,
              service: service.name
            });
            
            this.successfulService = service.name;
            bestResult = {
              ...result,
              serviceUsed: service.name,
              universalRequestCount: this.requestCount
            };
            break;
          } else {
            console.warn(`⚠️ [${debugId}] ${service.name} failed:`, result.error);
            lastError = new Error(`${service.name}: ${result.error}`);
          }
        } catch (error) {
          console.warn(`⚠️ [${debugId}] ${service.name} threw error:`, error.message);
          lastError = error;
          
          // Wait a bit before trying next service
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (bestResult) {
        return bestResult;
      } else {
        throw lastError || new Error("All image analysis services failed");
      }

    } catch (error) {
      console.error(`❌ [${debugId}] Universal analysis failed:`, {
        errorMessage: error.message,
        fileName: imageFile?.name,
        timestamp: new Date().toISOString()
      });
      
      return {
        success: false,
        error: error.message,
        fallbackDescription: this.generateFallbackDescription(imageFile),
        timestamp: new Date().toISOString(),
        debugId: debugId,
        serviceUsed: 'fallback',
        universalRequestCount: this.requestCount
      };
    } finally {
      this.isAnalyzing = false;
      console.log(`🏁 [${debugId}] Universal analysis complete`);
    }
  }

  /**
   * Get service order based on configuration and success history
   */
  getServiceOrder() {
    const services = [];

    // Prioritize based on configuration and past success
    if (this.successfulService === 'Google Vision' && this.googleVision.isConfigured()) {
      services.push({ name: 'Google Vision', analyzer: this.googleVision });
      services.push({ name: 'Hugging Face', analyzer: this.huggingFace });
    } else if (this.successfulService === 'Hugging Face') {
      services.push({ name: 'Hugging Face', analyzer: this.huggingFace });
      if (this.googleVision.isConfigured()) {
        services.push({ name: 'Google Vision', analyzer: this.googleVision });
      }
    } else {
      // Default order: Google Vision first (if configured), then Hugging Face
      if (this.googleVision.isConfigured()) {
        services.push({ name: 'Google Vision', analyzer: this.googleVision });
      }
      services.push({ name: 'Hugging Face', analyzer: this.huggingFace });
    }

    return services;
  }

  /**
   * Generate fallback description when all services fail
   */
  generateFallbackDescription(imageFile) {
    const fileName = imageFile.name.toLowerCase();
    const fileSize = (imageFile.size / 1024 / 1024).toFixed(1);
    
    // Smart fallback based on filename
    if (fileName.includes('before')) return `Before condition documentation (${fileSize}MB)`;
    if (fileName.includes('after')) return `After condition documentation (${fileSize}MB)`;
    if (fileName.includes('progress')) return `Construction progress documentation (${fileSize}MB)`;
    if (fileName.includes('issue') || fileName.includes('problem')) return `Issue documentation photo (${fileSize}MB)`;
    if (fileName.includes('complete')) return `Completed work documentation (${fileSize}MB)`;
    if (fileName.includes('foundation')) return `Foundation work documentation (${fileSize}MB)`;
    if (fileName.includes('electrical')) return `Electrical work documentation (${fileSize}MB)`;
    if (fileName.includes('plumbing')) return `Plumbing work documentation (${fileSize}MB)`;
    if (fileName.includes('roof')) return `Roofing work documentation (${fileSize}MB)`;
    if (fileName.includes('frame')) return `Framing work documentation (${fileSize}MB)`;
    
    return `Construction project documentation (${fileSize}MB)`;
  }

  /**
   * Check if any service is configured
   */
  isConfigured() {
    const googleConfigured = this.googleVision.isConfigured();
    const huggingFaceConfigured = this.huggingFace.isConfigured();
    const anyConfigured = googleConfigured || huggingFaceConfigured;
    
    console.log('🔍 Universal configuration check:', {
      googleVision: googleConfigured,
      huggingFace: huggingFaceConfigured,
      anyConfigured: anyConfigured,
      recommendedService: googleConfigured ? 'Google Vision' : 'Hugging Face'
    });
    
    return anyConfigured;
  }

  /**
   * Get comprehensive status
   */
  getStatus() {
    const googleStatus = this.googleVision.getStatus();
    const huggingFaceStatus = this.huggingFace.getStatus();
    
    return {
      configured: this.isConfigured(),
      analyzing: this.isAnalyzing,
      universalRequestCount: this.requestCount,
      lastSuccessfulService: this.successfulService,
      services: {
        googleVision: {
          configured: googleStatus.configured,
          requestCount: googleStatus.requestCount,
          service: googleStatus.service
        },
        huggingFace: {
          configured: huggingFaceStatus.configured,
          requestCount: huggingFaceStatus.requestCount,
          service: huggingFaceStatus.service
        }
      },
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Test all available services
   */
  async testConnection() {
    console.log('🧪 Testing all image analysis services...');
    
    const results = {
      universal: {
        success: false,
        services: {},
        recommendedService: null
      }
    };

    // Test Google Vision
    if (this.googleVision.isConfigured()) {
      try {
        console.log('🔍 Testing Google Vision...');
        const googleResult = await this.googleVision.testConnection();
        results.universal.services.googleVision = googleResult;
        
        if (googleResult.success) {
          results.universal.recommendedService = 'Google Vision';
          results.universal.success = true;
        }
      } catch (error) {
        results.universal.services.googleVision = {
          success: false,
          error: error.message
        };
      }
    }

    // Test Hugging Face
    try {
      console.log('🤗 Testing Hugging Face...');
      const huggingFaceResult = await this.huggingFace.testConnection();
      results.universal.services.huggingFace = huggingFaceResult;
      
      if (huggingFaceResult.success && !results.universal.recommendedService) {
        results.universal.recommendedService = 'Hugging Face';
        results.universal.success = true;
      }
    } catch (error) {
      results.universal.services.huggingFace = {
        success: false,
        error: error.message
      };
    }

    console.log('✅ Universal connection test complete:', results);
    return results;
  }

  /**
   * Get service recommendations
   */
  getRecommendations() {
    const recommendations = [];

    if (!this.googleVision.isConfigured()) {
      recommendations.push({
        type: 'setup',
        service: 'Google Vision',
        priority: 'high',
        message: 'Set up Google Cloud Vision API for best construction photo analysis',
        benefits: ['1000 free requests/month', 'Excellent object detection', 'No rate limits'],
        setup: 'Add REACT_APP_GOOGLE_VISION_API_KEY to your .env file'
      });
    }

    if (!this.huggingFace.apiKey) {
      recommendations.push({
        type: 'optimization',
        service: 'Hugging Face',
        priority: 'medium',
        message: 'Add Hugging Face API key for higher rate limits',
        benefits: ['Higher rate limits', 'Priority processing', 'More stable service'],
        setup: 'Add REACT_APP_HUGGINGFACE_API_KEY to your .env file (optional)'
      });
    }

    if (this.requestCount === 0) {
      recommendations.push({
        type: 'usage',
        service: 'Universal',
        priority: 'low',
        message: 'Test image analysis with a construction photo',
        benefits: ['Verify service functionality', 'Check description quality', 'Confirm setup'],
        setup: 'Upload a construction image to test the system'
      });
    }

    return recommendations;
  }

  /**
   * Reset service success tracking
   */
  resetServiceTracking() {
    this.successfulService = null;
    this.requestCount = 0;
    console.log('🔄 Service tracking reset');
  }
}

export default UniversalImageAnalyzer;

